(() => {
  const STORAGE_KEY = 'redelease_postal_code';
  const VALIDATED_STORAGE_KEY = 'redelease_validated_postal_code';
  const EVENT_NAME = 'redelease:postal-code';
  const VALIDATED_EVENT_NAME = 'redelease:postal-code-validated';

  const normalizePostalCode = (value) => (value || '').toString().replace(/\D/g, '').slice(0, 8);

  const formatPostalCode = (value) => {
    const digits = normalizePostalCode(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const getPostalCode = () => {
    try {
      return normalizePostalCode(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return '';
    }
  };

  const savePostalCode = (value) => {
    const postalCode = normalizePostalCode(value);
    if (postalCode.length !== 8) return false;

    try {
      const validatedPostalCode = normalizePostalCode(window.localStorage.getItem(VALIDATED_STORAGE_KEY));
      if (validatedPostalCode && validatedPostalCode !== postalCode) {
        window.localStorage.removeItem(VALIDATED_STORAGE_KEY);
      }
      window.localStorage.setItem(STORAGE_KEY, postalCode);
    } catch (error) {
      return false;
    }

    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: { postalCode },
      })
    );

    return true;
  };

  const getValidatedPostalCode = () => {
    try {
      return normalizePostalCode(window.localStorage.getItem(VALIDATED_STORAGE_KEY));
    } catch (error) {
      return '';
    }
  };

  const markPostalCodeValidated = (value) => {
    const postalCode = normalizePostalCode(value);
    if (postalCode.length !== 8) return false;

    try {
      window.localStorage.setItem(VALIDATED_STORAGE_KEY, postalCode);
    } catch (error) {
      return false;
    }

    document.dispatchEvent(
      new CustomEvent(VALIDATED_EVENT_NAME, {
        detail: { postalCode },
      })
    );

    return true;
  };

  const readConfig = () => {
    const configElement = document.getElementById('RedeleaseDeliveryConfig');
    if (!configElement) return {};

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Redelease delivery configuration is invalid', error);
      return {};
    }
  };

  const parsePostalRanges = (value) =>
    (value || '')
      .split(/\r?\n/)
      .map((line) => line.match(/\d{5}-?\d{3}/g))
      .filter((matches) => matches?.length >= 2)
      .map((matches) => [Number(normalizePostalCode(matches[0])), Number(normalizePostalCode(matches[1]))])
      .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && start <= end);

  const config = readConfig();
  const postalRanges = parsePostalRanges(config.postalRanges);

  const isEligiblePostalCode = (value) => {
    const postalCode = normalizePostalCode(value);
    if (postalCode.length !== 8) return false;

    const numericPostalCode = Number(postalCode);
    return postalRanges.some(([start, end]) => numericPostalCode >= start && numericPostalCode <= end);
  };

  window.RedeleaseDelivery = {
    eventName: EVENT_NAME,
    validatedEventName: VALIDATED_EVENT_NAME,
    formatPostalCode,
    getPostalCode,
    getValidatedPostalCode,
    isEligiblePostalCode,
    markPostalCodeValidated,
    normalizePostalCode,
    savePostalCode,
  };

  class RedeleaseSameDayMessage extends HTMLElement {
    connectedCallback() {
      this.titleElement = this.querySelector('[data-same-day-title]');
      this.prefixElement = this.querySelector('[data-same-day-prefix]');
      this.timeElement = this.querySelector('[data-same-day-time]');
      this.suffixElement = this.querySelector('[data-same-day-suffix]');
      this.handlePostalCodeChange = this.handlePostalCodeChange.bind(this);
      this.update = this.update.bind(this);

      document.addEventListener(EVENT_NAME, this.handlePostalCodeChange);
      window.addEventListener('storage', this.handlePostalCodeChange);
      this.update();
      this.timer = window.setInterval(this.update, 1000);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT_NAME, this.handlePostalCodeChange);
      window.removeEventListener('storage', this.handlePostalCodeChange);
      window.clearInterval(this.timer);
    }

    handlePostalCodeChange(event) {
      if (event.type === 'storage' && event.key !== STORAGE_KEY) return;
      this.update();
    }

    update() {
      const postalCode = getPostalCode();
      const enabled = config.enabled !== false;

      if (!enabled || !isEligiblePostalCode(postalCode)) {
        this.hidden = true;
        return;
      }

      const deadline = this.getDeadlineState();
      const cutoffLabel = this.getCutoffLabel();

      if (!deadline.isBusinessDay || deadline.remainingSeconds <= 0) {
        this.hidden = true;
        return;
      }

      if (deadline.remainingSeconds <= 3600) {
        this.setContent(
          'Entrega no mesmo dia disponível.',
          'Compre em até',
          this.formatCountdown(deadline.remainingSeconds),
          'e receba ainda hoje.'
        );
      } else {
        this.setContent(
          'Entrega no mesmo dia disponível.',
          'Compre até as',
          cutoffLabel,
          'e receba ainda hoje.'
        );
      }

      this.hidden = false;
    }

    setContent(title, prefix, time, suffix) {
      if (this.titleElement) this.titleElement.textContent = title;
      if (this.prefixElement) this.prefixElement.textContent = `${prefix} `;
      if (this.timeElement) this.timeElement.textContent = time;
      if (this.suffixElement) this.suffixElement.textContent = suffix === '.' ? suffix : ` ${suffix}`;
    }

    getDeadlineState() {
      const timeZone = config.timeZone || 'America/Sao_Paulo';
      const now = new Date();
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(now)
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, part.value])
      );
      const [cutoffHour, cutoffMinute] = this.getCutoffParts();
      const currentSeconds = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
      const cutoffSeconds = cutoffHour * 3600 + cutoffMinute * 60;

      return {
        isBusinessDay: parts.weekday !== 'Sat' && parts.weekday !== 'Sun',
        remainingSeconds: cutoffSeconds - currentSeconds,
      };
    }

    getCutoffParts() {
      const match = (config.cutoff || '13:00').match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return [13, 0];

      const hour = Math.min(Math.max(Number(match[1]), 0), 23);
      const minute = Math.min(Math.max(Number(match[2]), 0), 59);
      return [hour, minute];
    }

    getCutoffLabel() {
      const [hour, minute] = this.getCutoffParts();
      return minute === 0 ? `${hour}h` : `${hour}h${minute.toString().padStart(2, '0')}`;
    }

    formatCountdown(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.max(0, seconds % 60);

      return [hours, minutes, remainingSeconds].map((value) => value.toString().padStart(2, '0')).join(':');
    }
  }

  if (!customElements.get('redelease-same-day-message')) {
    customElements.define('redelease-same-day-message', RedeleaseSameDayMessage);
  }
})();
